/**
 * Usage data: every event the app may send with the person's consent, and the question each one answers. Nothing
 * else is sent, and nothing at all without consent (client/lib/usage.ts sends, server/routes/usage.ts checks again and
 * writes). docs/usage.md lists the same events and fields, and src/shared/usage.test.ts keeps the two in step.
 *
 * The rules, which the test checks too:
 * - A field is a closed list of values (z.enum) or a yes/no (z.boolean). Never free text, a name, a note, an id, a
 *   grind setting or an exact number: a number goes in as a bucket, a list of ranges ('0', '1', '2-5', '6+').
 * - An event has at most MAX_USAGE_FIELDS fields (an Analytics Engine data point holds 20 text fields, and five are
 *   taken: see usagePoint in server/usage.ts).
 * - A new field goes at the end of its event, so the queries over older data still read the same positions.
 *
 * A new feature the server can't see (the scale, the brew flow, the AI, which all happen in the browser) adds its
 * event here in the same PR. A new route needs nothing: Taretto Cloud counts every route by its pattern.
 */
import { z } from 'zod';
import { AI_PROVIDER_IDS } from './ai.js';
import { MACHINE_STOP_REASONS, METHOD_KINDS, SettingsSchema, WORKFLOW_STEP_KINDS } from './schemas.js';

/** A field of an event: its values and what it says, as the "What's sent" view shows it. */
export interface UsageField {
  schema: z.ZodEnum | z.ZodBoolean;
  about: string;
}

const oneOf = <const T extends readonly [string, ...string[]]>(values: T, about: string) => ({ schema: z.enum(values), about });
const yesNo = (about: string) => ({ schema: z.boolean(), about });

/** The app's pages, by the pattern of their address (an id stays out: /beans/:id, never /beans/12). */
export const USAGE_PAGES = [
  '/',
  '/brew',
  '/brews',
  '/brews/compare',
  '/brews/:id',
  '/beans',
  '/beans/new',
  '/beans/:id',
  '/beans/:id/edit',
  '/equipment',
  '/equipment/methods/new',
  '/equipment/methods/:id',
  '/scale',
  '/settings',
  '/ask',
  '*',
] as const;
export type UsagePage = (typeof USAGE_PAGES)[number];

/** The Bluetooth scale drivers (client/scale/registry.ts). */
export const USAGE_SCALES = ['bookoo', 'decent', 'felicita', 'acaia'] as const;
export type UsageScale = (typeof USAGE_SCALES)[number];

/** The browser, from its user agent, read on the device. */
export const USAGE_BROWSERS = ['bluefy', 'chrome', 'edge', 'samsung', 'opera', 'safari', 'firefox', 'other'] as const;
export type UsageBrowser = (typeof USAGE_BROWSERS)[number];

/** The kind of device, from the user agent and whether it has a touch screen. */
export const USAGE_DEVICES = ['ipad', 'iphone', 'android', 'mac', 'windows', 'linux', 'chromeos', 'other'] as const;
export type UsageDevice = (typeof USAGE_DEVICES)[number];

/** Why connecting to a scale failed, sorted from the browser's error (client/scale/bluetooth.ts connectFailure). */
export const USAGE_SCALE_FAILURES = ['bluetooth-off', 'blocked', 'unsupported', 'not-a-scale', 'unreachable', 'timeout', 'other'] as const;
export type UsageScaleFailure = (typeof USAGE_SCALE_FAILURES)[number];

/** Why connecting to a coffee machine failed, sorted from the error (client/machine/store.ts machineConnectFailure). */
export const USAGE_MACHINE_FAILURES = ['bluetooth-off', 'blocked', 'unsupported', 'not-a-machine', 'too-new', 'unreachable', 'timeout', 'other'] as const;

/** What a coffee machine answered a command (client/machine/types.ts MachineCommandResult), or that pairing failed. */
export const USAGE_MACHINE_RESULTS = ['ok', 'unsupported', 'invalid', 'busy', 'not_ready', 'fault', 'no_reply', 'not_paired'] as const;

/** The settings people change. timeZone is left out: the app fills it in from the browser. */
export const USAGE_SETTINGS = Object.keys(SettingsSchema.shape).filter((key) => key !== 'timeZone') as [
  Exclude<keyof typeof SettingsSchema.shape, 'timeZone'>,
  ...Array<Exclude<keyof typeof SettingsSchema.shape, 'timeZone'>>,
];

/** What the app asks the AI for: one per kind of question (the schemaName each sends), and Ask Taretto's chat. */
export const USAGE_AI_FEATURES = ['next_shot', 'tasting_notes', 'bag_recap', 'starting_point', 'bean_label', 'ask', 'test_key'] as const;
export type UsageAiFeature = (typeof USAGE_AI_FEATURES)[number];

/** The models Taretto was evaluated with (client/ai/providers.ts), and any other. */
export const USAGE_AI_MODELS = ['claude-sonnet-5-5', 'claude-haiku-5-5', 'claude-opus-5-5', 'gpt-6-luna', 'gpt-6.1-sol', 'other'] as const;
export type UsageAiModel = (typeof USAGE_AI_MODELS)[number];

/** How an AI request ended: answered, stopped by the person, or failed by kind (client/ai/types.ts AiErrorKind). */
export const USAGE_AI_OUTCOMES = [
  'answered',
  'stopped',
  'bad-key',
  'bad-model',
  'no-credit',
  'rate-limited',
  'unavailable',
  'offline',
  'timeout',
  'blocked',
  'refused',
  'bad-answer',
  'rejected',
  'other',
] as const;
export type UsageAiOutcome = (typeof USAGE_AI_OUTCOMES)[number];

/** The one change Suggest next shot proposes (client/ai/nextShot.ts NEXT_SHOT_CHANGES). */
export const USAGE_NEXT_SHOT_CHANGES = ['grind', 'dose', 'yield', 'temperature', 'preinfusion', 'none'] as const;

/** The guided brew's steps, with the milk steps a milk drink adds (client/workflow/types.ts FlowStepKind). */
export const USAGE_STEPS = [...WORKFLOW_STEP_KINDS, 'milk', 'drink'] as const;

const scale = oneOf(USAGE_SCALES, 'Which kind of scale');
const browser = oneOf(USAGE_BROWSERS, 'The browser');
const device = oneOf(USAGE_DEVICES, 'The kind of device');
const mode = oneOf(['guided', 'manual'], 'Guided by the scale, or logged by hand');
const method = oneOf(METHOD_KINDS, 'The kind of brew method');
const provider = oneOf(AI_PROVIDER_IDS, 'The AI provider');

export const USAGE_EVENTS = {
  page_viewed: {
    question: 'Which parts of the app get used.',
    fields: { page: oneOf(USAGE_PAGES, 'The page, by the pattern of its address, without ids') },
  },
  scale_connected: {
    question: 'Which scales people use, on which devices and browsers.',
    fields: {
      scale,
      browser,
      device,
      via: oneOf(['picker', 'reconnect', 'auto'], 'Picked in the browser, reconnected, or connected on its own at start'),
    },
  },
  scale_connect_failed: {
    question: 'How often connecting to a scale fails, and where.',
    fields: {
      scale: oneOf([...USAGE_SCALES, 'unknown'], 'Which kind of scale, when it got that far'),
      browser,
      device,
      via: oneOf(['picker', 'reconnect', 'auto'], 'Picked in the browser, reconnected, or connected on its own at start'),
      reason: oneOf(USAGE_SCALE_FAILURES, 'The kind of failure, sorted on the device from the browser’s error'),
    },
  },
  scale_dropped: {
    question: 'How often a connected scale drops out.',
    fields: { scale, browser, device },
  },
  machine_connected: {
    question: 'How many people connect a coffee machine, on which devices and browsers, and what their machines can do.',
    fields: {
      browser,
      device,
      via: oneOf(['picker', 'reconnect', 'auto'], 'Picked in the browser, reconnected, or connected on its own at start'),
      brew: yesNo('Whether the machine can start and stop a shot'),
      power: oneOf(['none', 'state', 'off', 'on'], 'What it can do with power: nothing, show it, also turn off, also turn on'),
      steam: yesNo('Whether it can switch to steam'),
    },
  },
  machine_connect_failed: {
    question: 'How often connecting to a coffee machine fails, and where.',
    fields: {
      browser,
      device,
      via: oneOf(['picker', 'reconnect', 'auto'], 'Picked in the browser, reconnected, or connected on its own at start'),
      reason: oneOf(USAGE_MACHINE_FAILURES, 'The kind of failure, sorted on the device from the browser’s error'),
    },
  },
  machine_dropped: {
    question: 'How often a connected coffee machine drops out.',
    fields: { browser, device },
  },
  machine_command: {
    question: 'Whether turning the machine on and off, starting and stopping the shot, and switching to steam and back works, and what gets in the way.',
    fields: {
      command: oneOf(['turn_on', 'turn_off', 'start_brew', 'stop_brew', 'enter_steam_mode', 'leave_steam_mode'], 'What was asked'),
      result: oneOf(USAGE_MACHINE_RESULTS, 'What the machine answered, or that pairing failed'),
      followed: yesNo('Whether the machine got there afterwards (a shot: within 3 s)'),
      via: oneOf(
        ['button', 'setup', 'home', 'after_save', 'cutoff', 'target', 'leave', 'shot_over', 'brew_step', 'drink', 'skip', 'exit', 'back'],
        'A tap (Turn on, Turn off, Start brew, Stop), Turn on on brew setup or Home, after saving a brew, at the cut-off cue, at the target, leaving the brew step mid-shot, and for steam: the shot over, the brew step done, milk in the drink, the drink step skipped, the brew left, or back to the shot',
      ),
    },
  },
  machine_steam: {
    question: 'How long machines take to heat for steam, and whether switching back to brew heat after a milk drink works.',
    fields: {
      ready: oneOf(['under_30s', '30_60s', '60_120s', 'over_120s', 'never'], 'How long after Taretto switched it to steam the machine showed steam ready'),
      ended: oneOf(['taretto', 'by_hand', 'machine'], 'Taretto switched it back, Taretto said to switch it off on the machine, or it ended on the machine first'),
    },
  },
  machine_shot: {
    question: 'How shots on a connected machine end: who started them, what stopped them, and how often something went wrong.',
    fields: {
      started: oneOf(['taretto', 'machine'], 'Start brew in Taretto, or the machine’s own button'),
      stopped: oneOf([...MACHINE_STOP_REASONS, 'restart', 'unknown'], 'What stopped it, as the machine reported it'),
      asked: yesNo('Whether Taretto had asked the machine to stop it'),
      late: yesNo('Whether stopping took over 3 s, so Taretto said to stop it by hand'),
      background: yesNo('Whether the page went to the background during the shot'),
    },
  },
  brew_started: {
    question: 'Whether people brew guided or by hand, and what they brew.',
    fields: {
      mode,
      method,
      milk: yesNo('A milk drink'),
      repeat: yesNo('Repeating an earlier brew'),
    },
  },
  brew_step_action: {
    question: 'Which steps of the guided brew get tared by hand, in the app or on the scale, skipped or redone (a sign that auto-tare guessed wrong).',
    fields: {
      step: oneOf(USAGE_STEPS, 'The step'),
      action: oneOf(['tare', 'empty', 'scale_tare', 'skip', 'restart', 'back', 'redo'], 'What was done'),
    },
  },
  brew_ended: {
    question: 'How many brews end without being saved.',
    fields: { mode, method, outcome: oneOf(['saved', 'discarded'], 'Saved, or thrown away') },
  },
  brew_recording: {
    question: 'Whether the recordings of guided brews get used, for bug reports.',
    fields: { action: oneOf(['downloaded', 'shared', 'deleted'], 'What was done with one, never what it holds') },
  },
  setting_changed: {
    question: 'Which settings people change from their defaults.',
    fields: {
      setting: oneOf(USAGE_SETTINGS, 'The setting’s name, never its value'),
      toDefault: yesNo('Changed back to the default'),
    },
  },
  ai_set_up: {
    question: 'How many people set up AI, with which provider and model.',
    fields: { provider, model: oneOf(USAGE_AI_MODELS, 'The model, when it is one Taretto was evaluated with') },
  },
  ai_request: {
    question: 'Which AI features get used, and how often they fail and why.',
    fields: {
      feature: oneOf(USAGE_AI_FEATURES, 'What was asked for'),
      provider,
      via: oneOf(['device', 'server'], 'With this device’s key, or through a self-hosted server’s'),
      outcome: oneOf(USAGE_AI_OUTCOMES, 'How it ended'),
    },
  },
  next_shot_suggested: {
    question: 'What Suggest next shot proposes, and how often people ask again.',
    fields: {
      change: oneOf(USAGE_NEXT_SHOT_CHANGES, 'The kind of change, never the setting it suggests'),
      again: yesNo('Asked again for the same brew'),
      rated: yesNo('The brew had a rating'),
    },
  },
} satisfies Record<string, { question: string; fields: Record<string, UsageField> }>;

export type UsageEvents = typeof USAGE_EVENTS;
export type UsageEventName = keyof UsageEvents;
export const USAGE_EVENT_NAMES = Object.keys(USAGE_EVENTS) as UsageEventName[];

/** An event's fields, as track() takes them. */
export type UsageFields<N extends UsageEventName> = {
  [K in keyof UsageEvents[N]['fields']]: UsageEvents[N]['fields'][K] extends { schema: infer S extends z.ZodType } ? z.infer<S> : never;
};

export const MAX_USAGE_FIELDS = 15;

/** The fields of an event as a schema that refuses anything else. */
export function usageFieldsSchema(name: UsageEventName): z.ZodObject {
  const fields: Record<string, UsageField> = USAGE_EVENTS[name].fields;
  return z.strictObject(Object.fromEntries(Object.entries(fields).map(([key, field]) => [key, field.schema])));
}

/** POST /api/usage/events: one event, as the app sends it. */
export const UsageEventSchema = z
  .strictObject({ name: z.enum(USAGE_EVENT_NAMES as [UsageEventName, ...UsageEventName[]]), fields: z.record(z.string(), z.unknown()) })
  .transform((event, ctx) => {
    const parsed = usageFieldsSchema(event.name).safeParse(event.fields);
    if (parsed.success) return { name: event.name, fields: parsed.data as Record<string, string | boolean> };
    for (const issue of parsed.error.issues) ctx.addIssue({ ...issue, path: ['fields', ...issue.path] });
    return z.NEVER;
  });
export type UsageEvent = z.output<typeof UsageEventSchema>;

/** An event's field values in the order the registry declares them, as text: what an Analytics Engine data point holds. */
export function usageFieldValues(event: UsageEvent): string[] {
  return Object.keys(USAGE_EVENTS[event.name].fields).map((key) => String(event.fields[key]));
}

/**
 * Whether this account shares usage data. Kept with the settings (server/repos/settings.ts) but not one of them: it
 * isn't in SettingsSchema, so get_settings and update_settings (MCP), the settings form and backups never carry it.
 */
export const UsageConsentSchema = z.object({
  share: z.boolean().default(false),
  /** When the person last answered, from the dashboard's question or the switch in Settings. Null until then. */
  answeredAt: z.string().min(1).nullable().default(null),
});
export type UsageConsent = z.infer<typeof UsageConsentSchema>;

/** GET /api/usage. */
export interface UsageStatus {
  /** Whether this server collects usage data at all. Without it the app neither asks nor sends anything. */
  collecting: boolean;
  share: boolean;
  answeredAt: string | null;
  /**
   * The random id this account's events carry while sharing is on, so the events of one account can be told apart
   * from many accounts'. Made when sharing is turned on, never from the account, the email or the WorkOS id. New on
   * request, and forgotten when sharing is turned off. Null while off.
   */
  id: string | null;
}

export const UsageConsentUpdateSchema = z.strictObject({ share: z.boolean() });
